import React, { useEffect, useState } from 'react';
import { adminApi, SepaRecall } from '../api/admin';

const NEXT_STATUSES: Record<SepaRecall['status'], SepaRecall['status'][]> = {
  REQUESTED: ['SENT_TO_PROVIDER'],
  SENT_TO_PROVIDER: ['ACCEPTED', 'REJECTED'],
  ACCEPTED: ['SETTLED', 'REJECTED'],
  REJECTED: [],
  SETTLED: [],
};

function SepaRecallsPage() {
  const [recalls, setRecalls] = useState<SepaRecall[]>([]);
  const [paymentId, setPaymentId] = useState('');
  const [reason, setReason] = useState('');
  const [message, setMessage] = useState('');

  const load = async () => {
    try {
      setRecalls((await adminApi.getSepaRecalls()).data || []);
    } catch (err: any) {
      setMessage(err.response?.data?.message || err.message || 'Failed to load SEPA recalls');
    }
  };

  useEffect(() => { void load(); }, []);

  const create = async (event: React.FormEvent) => {
    event.preventDefault();
    try {
      await adminApi.createSepaRecall(paymentId.trim(), reason.trim());
      setPaymentId('');
      setReason('');
      await load();
    } catch (err: any) {
      setMessage(err.response?.data?.message || err.message || 'Failed to create recall');
    }
  };

  const updateStatus = async (recall: SepaRecall, status: SepaRecall['status']) => {
    const providerReference = window.prompt('Provider reference (optional)') || undefined;
    const resolutionNote = status === 'REJECTED' || status === 'SETTLED' ? window.prompt('Resolution note') || undefined : undefined;
    const incomingProviderReturnId = status === 'SETTLED' ? window.prompt('Confirmed incoming provider return ID') || undefined : undefined;
    if (status === 'SETTLED' && !incomingProviderReturnId) return;
    try {
      await adminApi.updateSepaRecallStatus(recall.id, status, providerReference, resolutionNote, incomingProviderReturnId);
      await load();
    } catch (err: any) {
      setMessage(err.response?.data?.message || err.message || 'Failed to update recall');
    }
  };

  return (
    <div className="p-6">
      <h1 className="text-2xl font-semibold text-gray-900">SEPA Recalls</h1>
      <p className="mt-1 text-sm text-gray-600">Track provider recall requests. Settlement is recorded only after provider confirmation.</p>
      {message && <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{message}</div>}
      <form onSubmit={create} className="mt-6 flex flex-wrap gap-3 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
        <input value={paymentId} onChange={e => setPaymentId(e.target.value)} placeholder="Outgoing payment ID" required className="min-w-72 rounded-lg border border-gray-300 px-3 py-2 text-sm" />
        <input value={reason} onChange={e => setReason(e.target.value)} placeholder="Recall reason" required className="min-w-72 rounded-lg border border-gray-300 px-3 py-2 text-sm" />
        <button type="submit" className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white">Create recall</button>
      </form>
      <div className="mt-6 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
        <table className="w-full"><thead className="bg-gray-50"><tr><th className="px-4 py-3 text-left text-xs uppercase text-gray-500">Payment</th><th className="px-4 py-3 text-left text-xs uppercase text-gray-500">Reason</th><th className="px-4 py-3 text-left text-xs uppercase text-gray-500">Status</th><th className="px-4 py-3 text-left text-xs uppercase text-gray-500">Provider reference</th><th className="px-4 py-3" /></tr></thead>
          <tbody className="divide-y divide-gray-200">{recalls.map(recall => <tr key={recall.id}><td className="px-4 py-3 font-mono text-xs">{recall.outgoingPaymentId}</td><td className="px-4 py-3 text-sm">{recall.reason}</td><td className="px-4 py-3 text-sm">{recall.status}</td><td className="px-4 py-3 text-sm">{recall.providerReference || '—'}</td><td className="px-4 py-3 text-right">{NEXT_STATUSES[recall.status].map(status => <button key={status} onClick={() => void updateStatus(recall, status)} className="ml-2 text-sm font-medium text-blue-700">{status}</button>)}</td></tr>)}</tbody>
        </table>
      </div>
    </div>
  );
}

export default SepaRecallsPage;
